import React, { useState, useEffect } from 'react';
import { MessageSquarePlus, Check, X, Trash2, Filter, Inbox, Music, Clock, Mail, Bell } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, played: 0, declined: 0 });
  const [statusFilter, setStatusFilter] = useState('');

  const fetchRequests = (status = statusFilter) => {
    const params = status ? `?status=${status}` : '';
    axios.get(`${API}/admin/requests${params}`).then(r => {
      setRequests(r.data.requests || []);
      setCounts(r.data.counts || { pending: 0, played: 0, declined: 0 });
    }).catch(() => {});
  };

  useEffect(() => { fetchRequests(); }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/requests/${id}`, { status });
      toast.success(`Request marked as ${status}`);
      fetchRequests();
    } catch { toast.error('Failed to update'); }
  };

  const deleteRequest = async (id) => {
    try {
      await axios.delete(`${API}/admin/requests/${id}`);
      toast.success('Request deleted');
      fetchRequests();
    } catch { toast.error('Failed to delete'); }
  };

  const statusColors = {
    pending: 'text-amber-400 bg-amber-400/10',
    played: 'text-emerald-400 bg-emerald-400/10',
    declined: 'text-red-400 bg-red-400/10'
  };

  return (
    <div data-testid="admin-requests">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>Song Requests</h1>
          <p className="text-[#94A3B8] text-sm mt-1">Manage listener requests</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2.5 py-1 bg-amber-400/10 text-amber-400 rounded-full font-semibold">{counts.pending} pending</span>
          <span className="text-xs px-2.5 py-1 bg-emerald-400/10 text-emerald-400 rounded-full font-semibold">{counts.played} played</span>
          <span className="text-xs px-2.5 py-1 bg-red-400/10 text-red-400 rounded-full font-semibold">{counts.declined} declined</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { value: '', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'played', label: 'Played' },
          { value: 'declined', label: 'Declined' }
        ].map(tab => (
          <button
            key={tab.value}
            data-testid={`filter-${tab.value || 'all'}`}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              statusFilter === tab.value ? 'bg-[#D4AF37] text-[#0F1115]' : 'bg-[#171A21] text-[#94A3B8] hover:bg-white/10 border border-white/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className="text-center py-16 bg-[#171A21] rounded-2xl border border-white/5">
          <Inbox size={40} className="mx-auto text-[#94A3B8]/40 mb-3" />
          <p className="text-[#94A3B8]">No {statusFilter || ''} requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req, i) => (
            <div key={req.id} data-testid={`request-item-${i}`} className="bg-[#171A21] rounded-xl border border-white/5 p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                  <Music size={18} className="text-[#D4AF37]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-semibold">{req.song_name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${statusColors[req.status] || ''}`}>
                      {req.status}
                    </span>
                  </div>
                  {req.artist_name && <p className="text-[#D4AF37] text-sm">by {req.artist_name}</p>}
                  {req.message && <p className="text-[#94A3B8] text-sm mt-2 italic">"{req.message}"</p>}
                  <div className="flex items-center gap-3 mt-3 text-xs text-[#94A3B8] flex-wrap">
                    <span>From: <span className="text-white">{req.listener_name}</span></span>
                    {req.listener_email && (
                      <span className="flex items-center gap-1 text-blue-400">
                        <Mail size={10} /> {req.listener_email}
                        {req.notified && (
                          <span className="flex items-center gap-0.5 ml-1" title="Email notification sent">
                            <Bell size={10} className="text-emerald-400" />
                            <span className="text-emerald-400">sent</span>
                          </span>
                        )}
                      </span>
                    )}
                    <span className="flex items-center gap-1"><Clock size={10} /> {new Date(req.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {req.status === 'pending' && (
                    <>
                      <button data-testid={`play-request-${i}`} onClick={() => updateStatus(req.id, 'played')} className="p-2 rounded-lg hover:bg-emerald-500/20 text-[#94A3B8] hover:text-emerald-400 transition-colors" title="Mark as Played">
                        <Check size={16} />
                      </button>
                      <button data-testid={`decline-request-${i}`} onClick={() => updateStatus(req.id, 'declined')} className="p-2 rounded-lg hover:bg-red-500/20 text-[#94A3B8] hover:text-red-400 transition-colors" title="Decline">
                        <X size={16} />
                      </button>
                    </>
                  )}
                  {req.status !== 'pending' && (
                    <button data-testid={`revert-request-${i}`} onClick={() => updateStatus(req.id, 'pending')} className="p-2 rounded-lg hover:bg-amber-500/20 text-[#94A3B8] hover:text-amber-400 transition-colors text-xs" title="Revert to Pending">
                      Revert
                    </button>
                  )}
                  <button data-testid={`delete-request-${i}`} onClick={() => deleteRequest(req.id)} className="p-2 rounded-lg hover:bg-red-500/20 text-[#94A3B8] hover:text-red-400 transition-colors" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
